import type { DateTime } from "luxon";
import type {
	BreakContribution,
	HolidayContribution,
	HoursRateContribution,
	HoursRateScope,
	OvertimeContribution,
	PayBasis,
	RuleContribution,
	TrainingContribution,
	WageBreakdownResult,
	WageBreakdownRow,
} from "../../utils/models";
import { BONUS_RATE_TYPE, BREAK_RATE_TYPE } from "../../utils/wageRules/rateTypeTarget";
import { CENTS_PER_DOLLAR } from "./consts";

/*
 * DAT-906 · The wage breakdown panel's model: every figure it shows, in whole cents, arranged so that what
 * is on screen adds up exactly. The backend guarantees `baseExact + Σ amountPerHour === rate` at 4dp; each
 * figure is then rounded to the cent on its own, and those roundings need not cancel. Where they don't, a
 * named Rounding line carries the difference — the panel never shows parts that fail to reach their sum.
 *
 * Rounding can only explain a small gap: each figure moves by at most half a cent, so N rounded figures can
 * miss their sum by at most ⌈N/2⌉ cents. A bigger gap means a line is missing or wrong, and calling it
 * "Rounding" would hide exactly that. Such a row falls back to the rate-type view and the gap is reported.
 */
/** Digits `toFixed` keeps before rounding to the cent: enough to absorb float noise (1.005 × 100 = 100.49999…). */
const CENT_ROUNDING_GUARD_DIGITS = 6;

/** Half away from zero, on the cent — the same on both sides of zero so a deduction mirrors an addition. */
export const toCents = (dollars: number): number => {
	const cents = Number.parseFloat((Math.abs(dollars) * CENTS_PER_DOLLAR).toFixed(CENT_ROUNDING_GUARD_DIGITS));
	return Math.sign(dollars) * Math.round(cents);
};

/** Where the base line's own wording comes from: the pay basis, or `trainee`/`orientation`'s own line. */
export type BaseBasis = Exclude<PayBasis, "trainee" | "orientation">;

export type CompositionLine =
	/** The rate starts here. `basis` picks the wording ("Base wage", "Agency bill rate", …). */
	/**
	 * The rate starts here. `hoursRatesMatched`: whether any hours-rate rule is listed after it — an agency
	 * base says when none did, since the missing lines are otherwise the only sign.
	 */
	| { type: "base"; basis: BaseBasis; cents: number; hoursRatesMatched: boolean }
	/** Training / orientation pay replaced the base: this line IS the base, and says what it replaced. */
	| { type: "training"; contribution: TrainingContribution; cents: number; ownBaseCents: number }
	| { type: "hours_rate"; contribution: HoursRateContribution; cents: number }
	| { type: "overtime"; contribution: OvertimeContribution; cents: number; band: OvertimeBand }
	| { type: "holiday"; contribution: HolidayContribution; cents: number }
	| { type: "compounding"; cents: number }
	/** What the separately-rounded lines above miss of the rounded rate. Only ever present when non-zero. */
	| { type: "rounding"; cents: number };

/**
 * Where an overtime band's limit was crossed: `crossedAt` is the start of the shift's row whose counter
 * sat exactly on the limit — the moment it was crossed during this shift. Null when it was not crossed
 * here: then the week passed it before the shift (the line's `limitReachedAt` says when), or not
 * knowably at all.
 */
export type OvertimeBand = { crossedAt: DateTime | null };

/** A counter "on the limit": stored hours are 4dp, so an exact crossing matches within this. */
const LIMIT_MATCH_HOURS = 0.001;

/** The start of the shift's row where this weekly rule's counter crossed its limit, if it did so here. */
const weeklyCrossingIn = (rows: WageBreakdownRow[], c: OvertimeContribution): DateTime | null => {
	const crossing = rows.find((row) =>
		(row.contributions ?? []).some(
			(line) =>
				line.kind === "overtime" &&
				line.otKind === "weekly" &&
				line.ruleId === c.ruleId &&
				Math.abs(line.hoursBefore - line.thresholdHours) < LIMIT_MATCH_HOURS,
		),
	);
	return crossing?.startTime ?? null;
};

/** A gap rounding cannot explain: reported, and the figures it concerns are not itemized. */
export type CompositionMismatch =
	/** A row's lines miss its rate: that row is shown as a rate type only. */
	| { kind: "rate"; rowId: number; gapCents: number; lineCount: number }
	/** The segment wages miss the total: no Rounding line is drawn for it. */
	| { kind: "total"; gapCents: number; segmentCount: number };

export type WageSegment =
	| {
			type: "paid";
			row: WageBreakdownRow;
			rateCents: number;
			wageCents: number;
			/**
			 * `null` shows the row as a rate type only: a legacy row (stored before contributions were
			 * captured), or one whose lines miss its rate by more than rounding can explain.
			 */
			lines: CompositionLine[] | null;
	  }
	/** `rule` is `null` on a legacy row, or a recorded break no rule placed. */
	| { type: "break"; row: WageBreakdownRow; rule: BreakContribution | null }
	| { type: "bonus"; row: WageBreakdownRow; wageCents: number };

export type WageComposition = {
	segments: WageSegment[];
	totalCents: number;
	bonusCents: number;
	/** Σ segment wages (each rounded) misses the rounded total by this much; 0 when they agree or the gap is a mismatch. */
	wageRoundingCents: number;
	/** Some priced row is shown as a rate type only (legacy, or a mismatch), so its composition is not shown. */
	hasRateTypeOnlyRows: boolean;
	/** Gaps too big to be rounding, for the caller to report. */
	mismatches: CompositionMismatch[];
	/** The base every priced row shares (cents), or `undefined` when they differ — then each row says its own. */
	sharedBaseCents: number | undefined;
	/** A weekend-only hours-rate rule applied somewhere, so the shift has weekend hours. */
	hasWeekendHours: boolean;
};

const isTrainingLine = (c: RuleContribution): c is TrainingContribution => c.kind === "training" || c.kind === "orientation";

/** A rule that restricts nothing: all day, every role, job, employment type, union, agency and status. */
const isUnrestricted = (scope: HoursRateScope): boolean =>
	scope.startTime === null &&
	scope.endTime === null &&
	scope.weekend === null &&
	scope.isCharge === null &&
	scope.isUnion === null &&
	[scope.roles, scope.jobIds, scope.employmentTypes, scope.union, scope.trainingStatuses, scope.agency].every((list) => list.length === 0);

/**
 * The unnamed, unrestricted $0 hours-rate row the create form seeds, which many departments still carry: it
 * names nothing, limits nothing and adds nothing, so listing it would be a blank line reading "+$0.00". A
 * NAMED rule at $0 is kept — seeing that "Day · +$0.00" applied is exactly how someone checks their rules
 * matched — and so is an unnamed one with a scope, whose scope is the evidence of what it matched.
 */
const isBlankPlaceholder = (c: RuleContribution): boolean =>
	c.kind === "hours_rate" && c.name.trim() === "" && c.amountPerHour === 0 && isUnrestricted(c.scope);

/** Which base wording a row's pay basis needs. `null` basis only occurs with legacy rows, which show no lines. */
const baseBasisOf = (payBasis: PayBasis | null): BaseBasis => {
	switch (payBasis) {
		case "agency":
		case "salaried":
		case "estimated":
		case "override":
			return payBasis;
		// DEFAULT: a trainee/orientation row carries its own training line, which replaces this one. Were it
		// ever missing, the base is still the pay the nurse was given, so it reads as their base wage.
		case "trainee":
		case "orientation":
		case "employee":
		case null:
			return "employee";
	}
};

/** The most N separately-rounded figures can miss their rounded sum by: half a cent each, rounded up. */
export const maxRoundingGapCents = (figureCount: number): number => Math.ceil(figureCount / 2);

const contributionLine = (c: Exclude<RuleContribution, TrainingContribution | BreakContribution>, band: OvertimeBand): CompositionLine => {
	const cents = toCents(c.amountPerHour);
	switch (c.kind) {
		case "hours_rate":
			return { type: "hours_rate", contribution: c, cents };
		case "overtime":
			return { type: "overtime", contribution: c, cents, band };
		case "holiday":
			return { type: "holiday", contribution: c, cents };
		case "compounding":
			return { type: "compounding", cents };
	}
};

type LinesOrMismatch = { lines: CompositionLine[] } | { mismatch: CompositionMismatch };

/**
 * A priced row's lines, base first, closed by a Rounding line when the rounded parts miss the rounded rate
 * — or a mismatch, when they miss it by more than rounding can explain.
 */
const compositionLines = (
	row: WageBreakdownRow,
	contributions: RuleContribution[],
	baseExact: number,
	payBasis: PayBasis | null,
	shiftRows: WageBreakdownRow[],
): LinesOrMismatch => {
	const baseCents = toCents(baseExact);
	const training = contributions.find(isTrainingLine);
	const rest = contributions
		.filter((c): c is Exclude<RuleContribution, TrainingContribution | BreakContribution> => !isTrainingLine(c) && c.kind !== "break" && !isBlankPlaceholder(c))
		// A weekly band after the crossing starts where a rate boundary cut it, not where the limit was
		// crossed: the crossing is looked up across the whole shift.
		.map((c) => contributionLine(c, { crossedAt: c.kind === "overtime" && c.otKind === "weekly" ? weeklyCrossingIn(shiftRows, c) : null }));
	const first: CompositionLine = training
		? { type: "training", contribution: training, cents: baseCents, ownBaseCents: toCents(training.ownBase) }
		: { type: "base", basis: baseBasisOf(payBasis), cents: baseCents, hoursRatesMatched: rest.some((line) => line.type === "hours_rate") };
	const lines = [first, ...rest];
	const gapCents = toCents(row.rate) - lines.reduce((sum, line) => sum + line.cents, 0);
	if (Math.abs(gapCents) > maxRoundingGapCents(lines.length)) return { mismatch: { kind: "rate", rowId: row.id, gapCents, lineCount: lines.length } };
	return { lines: gapCents === 0 ? lines : [...lines, { type: "rounding", cents: gapCents }] };
};

type SegmentResult = { segment: WageSegment; mismatch?: CompositionMismatch };

const paidSegmentOf = (row: WageBreakdownRow, payBasis: PayBasis | null, shiftRows: WageBreakdownRow[]): SegmentResult => {
	const base = { type: "paid", row, rateCents: toCents(row.rate), wageCents: toCents(row.wage) } as const;
	if (row.contributions === null || row.baseExact === null) return { segment: { ...base, lines: null } };
	const result = compositionLines(row, row.contributions, row.baseExact, payBasis, shiftRows);
	return "mismatch" in result ? { segment: { ...base, lines: null }, mismatch: result.mismatch } : { segment: { ...base, lines: result.lines } };
};

const segmentOf = (row: WageBreakdownRow, payBasis: PayBasis | null, shiftRows: WageBreakdownRow[]): SegmentResult => {
	if (row.rateType === BREAK_RATE_TYPE) {
		const rule = row.contributions?.find((c): c is BreakContribution => c.kind === "break") ?? null;
		return { segment: { type: "break", row, rule } };
	}
	if (row.rateType === BONUS_RATE_TYPE) return { segment: { type: "bonus", row, wageCents: toCents(row.wage) } };
	return paidSegmentOf(row, payBasis, shiftRows);
};

/** The segment wages against the total: a Rounding amount within the cap, else a mismatch. */
const wageRounding = (rows: WageBreakdownRow[], totalCents: number): { wageRoundingCents: number; mismatch?: CompositionMismatch } => {
	const gapCents = totalCents - rows.reduce((sum, row) => sum + toCents(row.wage), 0);
	if (Math.abs(gapCents) <= maxRoundingGapCents(rows.length)) return { wageRoundingCents: gapCents };
	return { wageRoundingCents: 0, mismatch: { kind: "total", gapCents, segmentCount: rows.length } };
};

/** A weekend-only hours-rate rule applied somewhere, so the shift has weekend hours. */
const hasWeekendRule = (rows: WageBreakdownRow[]): boolean =>
	// DEFAULT: a legacy row (`contributions` null) names no rules, so it contributes no weekend evidence —
	// the same as a row whose rules include no weekend-only one.
	rows.some((row) => (row.contributions ?? []).some((c) => c.kind === "hours_rate" && c.scope.weekend === true));

/**
 * A priced row's base in cents: the exact one when captured, the stored 2dp column on a legacy row.
 * DEFAULT: `base` stands in for a missing `baseExact` — a legacy row has no exact base at all, and the 2dp
 * column is the figure the old panel showed for it, so the header's shared base reads the same as before.
 */
const baseCentsOf = (row: WageBreakdownRow): number => toCents(row.baseExact ?? row.base);

export const buildWageComposition = (breakdown: WageBreakdownResult): WageComposition => {
	const results = breakdown.rows.map((row) => segmentOf(row, breakdown.payBasis, breakdown.rows));
	const segments = results.map((result) => result.segment);
	const totalCents = toCents(breakdown.total);
	const total = wageRounding(breakdown.rows, totalCents);
	const paidSegments = segments.flatMap((segment) => (segment.type === "paid" ? [segment] : []));
	const distinctBases = [...new Set(paidSegments.map((segment) => baseCentsOf(segment.row)))];
	return {
		segments,
		totalCents,
		bonusCents: segments.reduce((sum, segment) => sum + (segment.type === "bonus" ? segment.wageCents : 0), 0),
		wageRoundingCents: total.wageRoundingCents,
		hasRateTypeOnlyRows: paidSegments.some((segment) => segment.lines === null),
		mismatches: [...results.flatMap((result) => (result.mismatch ? [result.mismatch] : [])), ...(total.mismatch ? [total.mismatch] : [])],
		sharedBaseCents: distinctBases.length === 1 ? distinctBases[0] : undefined,
		hasWeekendHours: hasWeekendRule(breakdown.rows),
	};
};

/**
 * An overtime rule another department holds: a float-pool nurse's OT follows their home department's rules.
 * Unknown (a line stored before its department was captured) counts as not — nothing to point the reader at.
 */
export const isOtherDepartmentRule = (c: OvertimeContribution, viewedDepartmentId: string): boolean =>
	c.departmentId !== null && c.departmentId !== viewedDepartmentId;

/** Whether any rule line of the shift comes from another department, so the panel explains its marker once. */
export const hasOtherDepartmentRule = (composition: WageComposition, viewedDepartmentId: string): boolean =>
	composition.segments.some(
		(segment) =>
			segment.type === "paid" && (segment.lines ?? []).some((line) => line.type === "overtime" && isOtherDepartmentRule(line.contribution, viewedDepartmentId)),
	);
